import React, { useState, useRef, useEffect } from 'react';

export const folio = (id) => `HN-${String(id).padStart(6, '0')}`;

// ---------- Teléfono mexicano (bandera + 52 fijo + 10 dígitos) ----------

// El valor se guarda siempre como "+52XXXXXXXXXX" (10 dígitos). El input solo deja
// escribir dígitos y los agrupa visualmente para que sea fácil de leer.
export function MexPhoneInput({ value, onChange, required }) {
  const digits = (value || '').replace(/^\+52/, '').replace(/\D/g, '').slice(0, 10);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    onChange(raw ? `+52${raw}` : '');
  };

  let formatted = digits;
  if (digits.length > 6) formatted = `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  else if (digits.length > 2) formatted = `${digits.slice(0, 2)} ${digits.slice(2)}`;

  return (
    <div className="mx-phone-input">
      <span className="mx-phone-flag">🇲🇽 +52</span>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        className="form-input mx-phone-number"
        value={formatted}
        onChange={handleChange}
        placeholder="55 1234 5678"
        required={required}
      />
    </div>
  );
}

export const isValidMxPhone = (value) => /^\+52\d{10}$/.test(value || '');

// Mismo algoritmo que isValidClabe() en server.js (18 dígitos + dígito verificador con
// pesos 3-7-1) — se duplica aquí nada más para poder avisar el error en el formulario
// antes de mandar la petición; el backend es quien de verdad decide si se guarda o no.
export function isValidClabe(clabe) {
  if (!/^\d{18}$/.test(clabe || '')) return false;
  const weights = [3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    sum += (parseInt(clabe[i], 10) * weights[i % 3]) % 10;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(clabe[17], 10);
}

// Estados de referral traducidos — usado en Dashboard y en el panel de admin
// para que nunca se muestre el nombre técnico crudo (confirmed_deal, etc.)
// pending_provider_response no trae "label" fijo — su texto se arma en statusLabel()
// con el nombre real de la empresa, en vez de un genérico "Empresa".
// no_deal_reported / deal_reported / disputed quedan solo por compatibilidad con
// referencias viejas de antes de quitar el paso de confirmación del referrer — ya
// no se generan para referencias nuevas.
export const STATUS_META = {
  pending_provider_response: { className: 'badge-warning' },
  no_deal_reported: { label: 'Sin negocio, pendiente de administración', className: 'badge-warning' },
  deal_reported: { label: 'Negocio reportado, pendiente de administración', className: 'badge-info' },
  confirmed_no_deal: { label: 'Confirmado: sin negocio', className: 'badge-neutral' },
  confirmed_deal: { label: 'Confirmado: con negocio', className: 'badge-success' },
  disputed: { label: 'En disputa, pendiente de administración', className: 'badge-danger' },
  paid: { label: 'Comisión pagada', className: 'badge-success' },
  rejected_duplicate: { label: 'Rechazada (duplicado)', className: 'badge-neutral' },
};

export function statusLabel(status, businessName) {
  if (status === 'pending_provider_response') {
    return businessName ? `Falta respuesta de ${businessName}` : 'Falta respuesta de la empresa';
  }
  return (STATUS_META[status] || {}).label || status;
}

export function StatusBadge({ status, businessName, small }) {
  const meta = STATUS_META[status] || { className: 'badge-neutral' };
  return <span className={`badge ${meta.className}${small ? ' badge-sm' : ''}`}>{statusLabel(status, businessName)}</span>;
}

// ---------- Ventana de detalle de una referencia (compartida: admin y miembros) ----------
// Toma el objeto de la referencia tal cual lo manda el backend. Si el referrer no debe ver
// montos, esos campos ya vienen ocultos desde el servidor (maskFinancials) — así que esta
// ventana nunca necesita saber quién la está viendo, solo muestra lo que sí llegó.

const money = (n) => `$${parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REFERRAL_FIELD_LABELS = {
  prospect_name: 'Prospecto',
  prospect_phone: 'Teléfono del prospecto',
  referrer_name: 'Referrer',
  provider_business_name: 'Empresa',
  service_name: 'Servicio',
  deal_amount: 'Monto del trato (sin IVA)',
  deal_commission_percentage: 'Comisión del servicio (%)',
  status: 'Estado',
  created_at: 'Creada',
  updated_at: 'Última actualización',
  possible_duplicate_of: 'Posible duplicado de',
  notes: 'Comentarios',
  provider_outcome: 'Resultado reportado por la empresa',
  provider_reported_at: 'Empresa reportó el',
  referrer_confirmation: 'Confirmación del referrer',
  referrer_confirmed_at: 'Referrer confirmó el',
  reminder_sent_at: 'Recordatorio enviado',
  member_commission: 'Comisión del referrer',
  hub_commission: 'Comisión del HUB',
  paid: 'Comisión pagada',
  payment_date: 'Fecha de pago',
};

// Traduce valores crudos que vienen tal cual de la base de datos (no solo el nombre del campo)
const REFERRAL_VALUE_TRANSLATIONS = {
  provider_outcome: { deal: 'Sí hubo negocio', no_deal: 'No hubo negocio' },
  referrer_confirmation: { confirmed: 'Confirmado', disputed: 'En disputa' },
};

const REFERRAL_MONEY_FIELDS = new Set(['deal_amount', 'member_commission', 'hub_commission']);

// Se muestran aunque estén vacíos — es la info central de la referencia
const REFERRAL_ALWAYS_SHOW = new Set([
  'status', 'prospect_name', 'prospect_phone', 'referrer_name',
  'provider_business_name', 'service_name', 'deal_amount', 'created_at',
]);

// IDs internos de otras tablas — ya se reflejan en los campos "_name", no aportan nada visualmente
const REFERRAL_HIDDEN_FIELDS = new Set(['provider_id', 'service_id', 'referrer_id', 'member_id', 'commission_id']);

const prettifyKey = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function formatReferralFieldValue(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (REFERRAL_VALUE_TRANSLATIONS[key] && REFERRAL_VALUE_TRANSLATIONS[key][value]) {
    return REFERRAL_VALUE_TRANSLATIONS[key][value];
  }
  if (REFERRAL_MONEY_FIELDS.has(key)) return money(value);
  if (key.endsWith('_at')) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-MX');
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

// actions (opcional): [{ label, onClick, variant: 'primary'|'secondary'|'danger' }, ...] —
// solo lo manda administración (ver AdminUsers.js). Un miembro normal nunca recibe este prop,
// así que su vista queda exactamente igual, solo lectura + Cerrar.
export function ReferralDetailModal({ referral, onClose, actions }) {
  if (!referral) return null;
  // Los campos vacíos no centrales se ocultan (disputas, recordatorios, confirmaciones que aún
  // no pasan) para que la ventana no se llene de "—" y quepa sin necesitar scroll.
  const entries = Object.entries(referral).filter(([key, value]) => {
    if (key === 'id' || REFERRAL_HIDDEN_FIELDS.has(key)) return false;
    if (REFERRAL_ALWAYS_SHOW.has(key)) return true;
    return value !== null && value !== undefined && value !== '';
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box detail-box" role="dialog" aria-modal="true">
        <div className="detail-header">
          <h3>Referencia {folio(referral.id)}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cerrar</button>
        </div>
        <div className="detail-grid">
          {entries.map(([key, value]) => (
            <div className={key === 'prospect_name' || key === 'status' ? 'detail-field-full' : ''} key={key}>
              <div className="detail-label">{REFERRAL_FIELD_LABELS[key] || prettifyKey(key)}</div>
              <div className={`detail-value${REFERRAL_MONEY_FIELDS.has(key) ? ' detail-value-money' : ''}`}>
                {key === 'status' ? <StatusBadge status={value} businessName={referral.provider_business_name} /> : formatReferralFieldValue(key, value)}
              </div>
            </div>
          ))}
        </div>
        {actions && actions.length > 0 && (
          <div className="detail-actions">
            <div className="detail-actions-label">Acciones de administración</div>
            <div className="detail-actions-row">
              {actions.map((a) => (
                <button
                  key={a.label}
                  className={`btn btn-sm btn-${a.variant || 'secondary'}`}
                  onClick={a.onClick}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Esqueletos de carga (reemplazan el texto plano "Cargando...") ----------

export function SkeletonKpiRow({ count = 3 }) {
  return (
    <div className="kpi-row">
      {Array.from({ length: count }).map((_, i) => (
        <div className="stat-tile" key={i}>
          <div className="skeleton-block" style={{ width: '60%', height: '11px', marginBottom: '10px' }} />
          <div className="skeleton-block" style={{ width: '40%', height: '22px' }} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 5 }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><div className="skeleton-block" style={{ width: c === 0 ? '60%' : '85%', height: '13px' }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonPage() {
  return (
    <div className="page">
      <div className="skeleton-block" style={{ width: '220px', height: '28px', marginBottom: '8px' }} />
      <div className="skeleton-block" style={{ width: '320px', height: '14px', marginBottom: '28px' }} />
      <SkeletonKpiRow />
      <div style={{ marginTop: '20px' }}>
        <SkeletonTable />
      </div>
    </div>
  );
}

// ---------- Eventos: Coffee HUB / Junta Mensual ----------

// Valores de arranque por si el fetch de /api/event-types aún no llega — el catálogo real
// ahora lo administra Admin (puede agregar tipos nuevos), pero se necesita algo antes de esa
// primera respuesta. mergeEventTypeLabels() se llama una vez al cargar sesión (ver App.js) y
// va agregando lo que exista en la base; como es el mismo objeto en memoria, cualquier pantalla
// que lea EVENT_TYPE_LABELS[key] en su render ve las etiquetas nuevas sin tener que refetchear.
export const EVENT_TYPE_LABELS = { cafe_hub: 'Coffee HUB', junta_mensual: 'Junta Mensual' };
export const EVENT_MODALITY_LABELS = { fisico: 'Presencial', virtual: 'Virtual' };
// Traducción de los valores crudos de users.status/role — antes se mostraban tal cual venían
// de la base ("active", "owner"...) sin pasar por ningún label, a diferencia del resto de los
// estados del sitio que sí tienen su mapa de traducción.
export const USER_STATUS_LABELS = { active: 'Activo', pending: 'Pendiente', rejected: 'Rechazado', inactive: 'Inactivo' };
export const USER_ROLE_LABELS = { member: 'Miembro', admin: 'Administrador', owner: 'Dueño' };
// Metadata de cada tipo de evento (qué modalidades acepta, si admite invitados) — antes esto
// estaba hardcodeado en el código de cada pantalla (ej. Invitados.js solo dejaba ligar
// "junta_mensual"), lo que rompía en cuanto Admin creaba un tipo nuevo. Ahora cualquier
// pantalla puede preguntar EVENT_TYPE_META[key]?.allows_invitados en vez de comparar contra
// un nombre fijo.
export const EVENT_TYPE_META = {
  cafe_hub: { allows_fisico: true, allows_virtual: true, allows_invitados: true },
  junta_mensual: { allows_fisico: true, allows_virtual: true, allows_invitados: true },
};
export function mergeEventTypeLabels(list) {
  (list || []).forEach(t => {
    EVENT_TYPE_LABELS[t.key] = t.label;
    EVENT_TYPE_META[t.key] = {
      allows_fisico: t.allows_fisico !== false,
      allows_virtual: t.allows_virtual !== false,
      allows_invitados: t.allows_invitados !== false,
    };
  });
}

// El backend puede mandar la fecha como "YYYY-MM-DD" o como ISO completo (columnas DATE de
// Postgres llegan como Date -> se serializan a ISO con hora en Express, ej. "...T00:00:00.000Z").
// BUG que se corrige aquí: cuando llega con "Z" (UTC), new Date(...) + toLocaleDateString con
// la zona horaria LOCAL del navegador (México, UTC-6) restaba horas y mostraba el día anterior.
// Se arma la fecha en UTC a partir de los componentes y se formatea también en UTC, así nunca
// se cruza con la zona horaria de quien la ve.
export const formatEventDate = (d) => {
  if (!d) return '';
  const raw = String(d);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return raw;
  const [, y, m, day] = match;
  const date = new Date(Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(day, 10)));
  return isNaN(date.getTime())
    ? raw
    : date.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'UTC' });
};

// Formatea "HH:MM" (24h, como lo guarda el <input type="time">) a "1:30 p. m."
export const formatEventTime = (t) => {
  if (!t) return '';
  const match = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return t;
  const date = new Date();
  date.setHours(parseInt(match[1], 10), parseInt(match[2], 10), 0, 0);
  return date.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// ---------- Agregar al calendario (Google / Outlook.com / .ics) ----------

const pad2 = (n) => String(n).padStart(2, '0');

// event_date puede llegar como "YYYY-MM-DD" o como ISO completo (ver formatEventDate arriba).
// event_time es "HH:MM" (del <input type="time">) — si no hay hora se asume 9:00 am.
function eventStartEnd(ev, durationHours = 1) {
  const dateStr = String(ev.event_date).slice(0, 10);
  const timeStr = ev.event_time && /^\d{1,2}:\d{2}/.test(ev.event_time) ? ev.event_time.slice(0, 5) : '09:00';
  const start = new Date(`${dateStr}T${timeStr}:00`);
  const end = new Date(start.getTime() + durationHours * 3600000);
  return { start, end };
}

const toUtcStamp = (date) => date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

export function buildCalendarLinks(ev) {
  const { start, end } = eventStartEnd(ev);
  const title = `${EVENT_TYPE_LABELS[ev.event_type] || ev.event_type} — ${EVENT_MODALITY_LABELS[ev.modality] || ev.modality}`;
  const details = ev.notes || '';
  const location = ev.location_or_link || '';

  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${toUtcStamp(start)}/${toUtcStamp(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;

  const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&subject=${encodeURIComponent(title)}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//PYMES Networking HUB//ES',
    'BEGIN:VEVENT',
    `UID:hub-event-${ev.id}-${start.getTime()}@hubnetworking.com.mx`,
    `DTSTAMP:${toUtcStamp(new Date())}`,
    `DTSTART:${toUtcStamp(start)}`,
    `DTEND:${toUtcStamp(end)}`,
    `SUMMARY:${title}`,
    details ? `DESCRIPTION:${details.replace(/\r?\n/g, '\\n')}` : '',
    location ? `LOCATION:${location.replace(/\r?\n/g, ' ')}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  return { google, outlook, ics };
}

export function downloadIcs(ev) {
  const { ics } = buildCalendarLinks(ev);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `evento-${ev.id}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4" /><path d="M8 3v4" /><path d="M3 10h18" />
  </svg>
);

export function AddToCalendarButton({ event, dark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const links = buildCalendarLinks(event);

  return (
    <div className="add-cal-wrap" ref={ref}>
      <button type="button" className={`add-cal-btn${dark ? ' add-cal-btn-dark' : ''}`} onClick={() => setOpen(o => !o)}>
        <CalendarIcon /> Agregar al calendario
      </button>
      {open && (
        <div className="add-cal-menu">
          <a href={links.google} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>Google Calendar</a>
          <a href={links.outlook} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>Outlook.com</a>
          <button type="button" onClick={() => { downloadIcs(event); setOpen(false); }}>Descargar .ics (Apple / Outlook de escritorio)</button>
        </div>
      )}
    </div>
  );
}

// Tarjetas con las próximas fechas de café hub (física/virtual) y junta mensual, con
// autoregistro de asistencia. Se usa en el Dashboard del miembro.
export function UpcomingEventsPanel({ events, attendance, onOpenCheckin, onCancelCheckin }) {
  if (!events || events.length === 0) {
    return (
      <div className="event-banner event-banner-empty">
        <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>Próximos eventos</h3>
        <p style={{ margin: 0, fontSize: '13px' }}>Administración aún no ha publicado la próxima fecha.</p>
      </div>
    );
  }
  const attendedIds = new Set((attendance || []).map(a => a.event_id));
  return (
    <div className="event-banner">
      <div className="event-banner-glow" />
      <div className="event-banner-title">Próximos eventos</div>
      <div className="event-banner-grid">
        {events.map(ev => {
          const attended = attendedIds.has(ev.id);
          return (
            <div className="event-banner-card" key={ev.id}>
              <div>
                <h4>{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type} — {EVENT_MODALITY_LABELS[ev.modality] || ev.modality}</h4>
                <p>{formatEventDate(ev.event_date)}</p>
                {ev.event_time && <p className="event-banner-time">{formatEventTime(ev.event_time)}</p>}
                {ev.location_or_link && <p className="event-banner-loc">{ev.location_or_link}</p>}
              </div>
              {attended ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <span className="event-banner-done">✓ Ya te registraste</span>
                  <button className="event-banner-cancel-btn" onClick={() => onCancelCheckin(ev)}>Cancelar asistencia</button>
                </div>
              ) : (
                <button className="event-banner-checkin-btn" onClick={() => onOpenCheckin(ev)}>Registrar asistencia</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Modal de check-in: confirma asistencia, agrega al calendario y opcionalmente
// declara el invitado que se lleva a ese evento (nuevo o uno ya registrado antes). ----------

export function CheckinModal({ event, myInvitados, onClose, onConfirm }) {
  const [bringsGuest, setBringsGuest] = useState('no'); // 'no' | 'existing' | 'new'
  const [selectedInvitadoId, setSelectedInvitadoId] = useState('');
  const [newGuest, setNewGuest] = useState({ name: '', business_name: '', giro: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [guestError, setGuestError] = useState(null);

  if (!event) return null;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleConfirm = async () => {
    setGuestError(null);
    if (bringsGuest === 'existing' && !selectedInvitadoId) return;
    if (bringsGuest === 'new') {
      if (!newGuest.name.trim()) return setGuestError('El nombre del invitado es requerido.');
      if (!newGuest.business_name.trim()) return setGuestError('El negocio del invitado es requerido.');
      if (!newGuest.giro.trim()) return setGuestError('El giro del negocio es requerido.');
      if (!isValidMxPhone(newGuest.phone)) return setGuestError('Escribe un teléfono mexicano válido (10 dígitos).');
      if (!EMAIL_RE.test(newGuest.email)) return setGuestError('Escribe un correo válido.');
    }
    setSaving(true);
    try {
      await onConfirm({
        invitadoId: bringsGuest === 'existing' ? selectedInvitadoId : null,
        newGuest: bringsGuest === 'new' ? newGuest : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" role="dialog" aria-modal="true">
        <p className="modal-message" style={{ marginBottom: '4px' }}>
          {EVENT_TYPE_LABELS[event.event_type] || event.event_type} — {EVENT_MODALITY_LABELS[event.modality] || event.modality}
        </p>
        <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--neutral-500)', textTransform: 'capitalize' }}>
          {formatEventDate(event.event_date)}{event.event_time ? ` · ${formatEventTime(event.event_time)}` : ''}
        </p>

        <div className="form-group" style={{ marginTop: '4px' }}>
          <label className="form-label">¿Llevas invitado?</label>
          <select className="form-select" value={bringsGuest} onChange={(e) => setBringsGuest(e.target.value)}>
            <option value="no">No</option>
            <option value="existing">Sí, uno que ya había registrado</option>
            <option value="new">Sí, uno nuevo</option>
          </select>
        </div>

        {bringsGuest === 'existing' && (
          <div className="form-group">
            <label className="form-label">Elige el invitado</label>
            <select className="form-select" value={selectedInvitadoId} onChange={(e) => setSelectedInvitadoId(e.target.value)}>
              <option value="">Selecciona...</option>
              {(myInvitados || []).map(i => (
                <option key={i.id} value={i.id}>{i.name}{i.business_name ? ` — ${i.business_name}` : ''}</option>
              ))}
            </select>
            {(myInvitados || []).length === 0 && <span className="form-hint">Aún no tienes invitados registrados — elige "uno nuevo".</span>}
          </div>
        )}

        {bringsGuest === 'new' && (
          <div>
            {guestError && <div className="msg-banner error">{guestError}</div>}
            <div className="form-group">
              <label className="form-label">Nombre*</label>
              <input className="form-input" value={newGuest.name} onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })} autoFocus required />
            </div>
            <div className="form-group">
              <label className="form-label">Negocio*</label>
              <input className="form-input" value={newGuest.business_name} onChange={(e) => setNewGuest({ ...newGuest, business_name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Giro del negocio*</label>
              <input className="form-input" value={newGuest.giro} onChange={(e) => setNewGuest({ ...newGuest, giro: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono*</label>
              <MexPhoneInput value={newGuest.phone} onChange={(v) => setNewGuest({ ...newGuest, phone: v })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email*</label>
              <input className="form-input" type="email" value={newGuest.email} onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })} required />
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Guardando...' : 'Registrar asistencia'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Paginación simple ----------

export function paginate(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function Pagination({ page, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Anterior</button>
      <span className="pagination-info">Página {page} de {totalPages} ({totalItems} en total)</span>
      <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Siguiente →</button>
    </div>
  );
}

// ---------- Orden de tablas por columna ----------

// getValue puede ser el nombre de un campo o una función (row) => valor comparable
export function sortRows(rows, sort, getValue) {
  if (!sort || !sort.key) return rows;
  const extract = typeof getValue === 'function' ? getValue : (row) => row[sort.key];
  const sorted = [...rows].sort((a, b) => {
    let va = extract(a, sort.key);
    let vb = extract(b, sort.key);
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return sort.dir === 'asc' ? -1 : 1;
    if (va > vb) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
}

export function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort && sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Ordenar"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          style={{ opacity: active ? 1 : 0.3, transform: active && sort.dir === 'desc' ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }}>
          <path d="M6 15l6 6 6-6" />
          <path d="M6 9l6-6 6 6" />
        </svg>
      </span>
    </th>
  );
}

// Agrupar por columna — visualmente distinto de SortTh (ordena) para que quede claro
// que no ordena filas, las junta bajo un encabezado por cada valor de esa columna.
// Solo un "agrupar por" puede estar activo a la vez (click de nuevo lo apaga).
export function GroupTh({ label, groupKey, groupBy, onGroup }) {
  const active = groupBy === groupKey;
  return (
    <th
      onClick={() => onGroup(active ? null : groupKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Agrupar por"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
          style={{ opacity: active ? 1 : 0.3 }}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      </span>
    </th>
  );
}

// ---------- Exportar CSV ----------

function csvEscape(val) {
  const s = val === null || val === undefined ? '' : String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// columns: [{ key: 'name', label: 'Nombre' }, ...] o [{ key: (row) => row.a + row.b, label: '...' }]
export function exportCSV(filename, columns, rows) {
  const header = columns.map(c => csvEscape(c.label)).join(',');
  const lines = rows.map(row =>
    columns.map(c => csvEscape(typeof c.key === 'function' ? c.key(row) : row[c.key])).join(',')
  );
  const csv = '﻿' + [header, ...lines].join('\n'); // BOM para que Excel abra bien los acentos
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportCsvButton({ filename, columns, rows, label = 'Exportar CSV' }) {
  return (
    <button
      className="btn btn-secondary btn-sm"
      onClick={() => exportCSV(filename, columns, rows)}
      disabled={!rows || rows.length === 0}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v13" />
        <path d="M6 11l6 6 6-6" />
        <path d="M4 21h16" />
      </svg>
      {label}
    </button>
  );
}

export function StatTile({ label, value, onClick }) {
  return (
    <div
      className={`stat-tile${onClick ? ' stat-tile-clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="stat-tile-label">{label}</div>
      <div className="stat-tile-value">{value}</div>
    </div>
  );
}

const STATUS_ORDER = [
  { key: 'pending_provider_response', label: 'Falta respuesta de la empresa', color: 'var(--status-warning)' },
  { key: 'no_deal_reported', label: 'Sin negocio, pendiente de administración', color: 'var(--status-warning)' },
  { key: 'deal_reported', label: 'Negocio reportado, pendiente de administración', color: 'var(--status-warning)' },
  { key: 'confirmed_deal', label: 'Confirmado: con negocio', color: 'var(--status-good)' },
  { key: 'paid', label: 'Pagada', color: 'var(--status-good)' },
  { key: 'confirmed_no_deal', label: 'Confirmado: sin negocio', color: 'var(--status-neutral)' },
  { key: 'disputed', label: 'En disputa, pendiente de administración', color: 'var(--status-critical)' },
  { key: 'rejected_duplicate', label: 'Duplicado rechazado', color: 'var(--status-neutral)' },
];

// Barras horizontales por estado — recibe [{status, count}]
// onSelect(statusKey) opcional: si se pasa, cada barra es clickeable (para filtrar una tabla, etc.)
export function StatusBarChart({ title, subtitle, data, onSelect }) {
  const counts = {};
  data.forEach(d => { counts[d.status] = parseInt(d.count, 10); });
  const rows = STATUS_ORDER.filter(s => counts[s.key] > 0);
  const max = Math.max(1, ...rows.map(r => counts[r.key]));

  return (
    <div className="chart-panel">
      <h3 className="chart-panel-title">{title}</h3>
      {subtitle && <p className="chart-panel-subtitle">{subtitle}</p>}
      {rows.length === 0 && <p style={{ color: 'var(--neutral-500)', fontSize: '14px' }}>Sin datos todavía</p>}
      {rows.map(r => (
        <div
          className={`hbar-row${onSelect ? ' hbar-row-clickable' : ''}`}
          key={r.key}
          onClick={onSelect ? () => onSelect(r.key) : undefined}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          title={onSelect ? `Ver referencias: ${r.label}` : undefined}
        >
          <div className="hbar-label">{r.label}</div>
          <div className="hbar-track">
            <div className="hbar-fill" style={{ width: `${(counts[r.key] / max) * 100}%`, background: r.color }} />
          </div>
          <div className="hbar-count">{counts[r.key]}</div>
        </div>
      ))}
    </div>
  );
}

// Línea de tendencia simple con eje vertical — recibe [{week, count}]
export function TrendLine({ title, subtitle, data }) {
  const points = data.map(d => ({ label: new Date(d.week).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }), count: parseInt(d.count, 10) }));
  const width = 560;
  const height = 160;
  const paddingLeft = 34;
  const paddingRight = 12;
  const paddingTop = 14;
  const paddingBottom = 26;
  const max = Math.max(1, ...points.map(p => p.count));
  const steps = 4;
  const gridValues = Array.from({ length: steps + 1 }, (_, i) => Math.round((max / steps) * i));

  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;

  const coords = points.map((p, i) => {
    const x = points.length > 1 ? paddingLeft + (i / (points.length - 1)) * plotWidth : paddingLeft + plotWidth / 2;
    const y = paddingTop + plotHeight - (p.count / max) * plotHeight;
    return { ...p, x, y };
  });

  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');

  return (
    <div className="chart-panel">
      <h3 className="chart-panel-title">{title}</h3>
      {subtitle && <p className="chart-panel-subtitle">{subtitle}</p>}
      {coords.length === 0 ? (
        <p style={{ color: 'var(--neutral-500)', fontSize: '14px' }}>Sin datos todavía</p>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', overflow: 'hidden' }}>
          {gridValues.map((v, i) => {
            const y = paddingTop + plotHeight - (v / max) * plotHeight;
            return (
              <g key={i}>
                <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="var(--neutral-100)" strokeWidth="1" />
                <text x={paddingLeft - 8} y={y + 3} fontSize="10" textAnchor="end" fill="var(--neutral-500)">{v}</text>
              </g>
            );
          })}
          <line x1={paddingLeft} y1={paddingTop + plotHeight} x2={width - paddingRight} y2={paddingTop + plotHeight} stroke="var(--neutral-200)" strokeWidth="1" />
          <path d={path} fill="none" stroke="var(--chart-sequential)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {coords.map((c, i) => (
            <circle key={i} cx={c.x} cy={c.y} r="4" fill="var(--chart-sequential)" stroke="white" strokeWidth="1.5" />
          ))}
          {coords.map((c, i) => {
            // Las etiquetas de las puntas se anclan hacia adentro para que no se corten contra el borde del gráfico
            const anchor = i === 0 ? 'start' : i === coords.length - 1 ? 'end' : 'middle';
            return (
              <text key={i} x={c.x} y={height - 6} fontSize="10" textAnchor={anchor} fill="var(--neutral-500)">{c.label}</text>
            );
          })}
        </svg>
      )}
    </div>
  );
}

export function RankList({ title, subtitle, items, nameKey, countKey, avatarKey = 'avatar_url' }) {
  return (
    <div className="chart-panel">
      <h3 className="chart-panel-title">{title}</h3>
      {subtitle && <p className="chart-panel-subtitle">{subtitle}</p>}
      {items.length === 0 ? (
        <p style={{ color: 'var(--neutral-500)', fontSize: '14px' }}>Sin datos todavía</p>
      ) : (
        <ul className="rank-list">
          {items.map((item, i) => (
            <li key={i}>
              <span className="rank-name" style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <Avatar src={item[avatarKey]} seed={item[nameKey]} size={22} />
                {item[nameKey] || '—'}
              </span>
              <span className="rank-count">{item[countKey]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Avatares (foto de internet como placeholder — persona real vía pravatar, respaldo con iniciales) ----------

export function Avatar({ src, seed, size = 28 }) {
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(seed || '?')}&background=2563eb&color=fff&bold=true&size=64`;
  const url = src || `https://i.pravatar.cc/64?u=${encodeURIComponent(seed || 'hub-networking')}`;
  return (
    <img
      src={url}
      alt=""
      className="avatar-img"
      style={{ width: size, height: size, minWidth: size }}
      onError={(e) => { if (e.target.src !== fallback) e.target.src = fallback; }}
    />
  );
}
